import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
} from "react";
import { GenerateReportModal } from "../modals/GenerateReportModal";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";
import { useGetReports } from "../hooks/useGetReports";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useRouter } from "expo-router";

interface Context {
  showGenerateReportModal: (initialResident?: ResidentModel) => void;
  reports: ReturnType<typeof useGetReports>["reports"];
  areReportsLoading: boolean;
  newReportIds: number[];
}

interface Props {
  children: React.ReactNode;
}

const ReportGenerationContext = createContext<Context>({} as Context);

const NEW_REPORT_BADGE_TIMEOUT_MS = 30000;

export function ReportGenerationProvider({ children }: Props) {
  const modalRef = useRef<React.ComponentRef<typeof GenerateReportModal>>(null);
  const router = useRouter();
  const { showNewToast } = useDisplayToast();

  const handleReportsReady = () => {
    showNewToast({
      title: "Your reports are ready",
      message: "Click here to view and download your reports",
      type: "success",
      onPress: () => {
        router.push("/reports");
      },
    });
  };
  // Poll for reports
  const { reports, areReportsLoading } = useGetReports({
    onReportsReady: handleReportsReady,
  });

  // Track new report IDs for badge display
  const newReportTimeoutRef = useRef<NodeJS.Timeout>();
  const [newReportIds, setNewReportIds] = useState<number[]>([]);

  const handleNewReports = (ids: number[]) => {
    setNewReportIds((prev) => [...new Set([...prev, ...ids])]);

    if (newReportTimeoutRef.current) {
      clearTimeout(newReportTimeoutRef.current);
    }

    newReportTimeoutRef.current = setTimeout(() => {
      setNewReportIds([]);
    }, NEW_REPORT_BADGE_TIMEOUT_MS);
  };

  useEffect(() => {
    return () => {
      if (newReportTimeoutRef.current) {
        clearTimeout(newReportTimeoutRef.current);
      }
    };
  }, []);

  const showGenerateReportModal = (initialResident?: ResidentModel) => {
    modalRef.current?.show(initialResident);
  };

  return (
    <ReportGenerationContext.Provider
      value={{
        showGenerateReportModal,
        reports,
        areReportsLoading,
        newReportIds,
      }}
    >
      {children}
      <GenerateReportModal ref={modalRef} onSuccess={handleNewReports} />
    </ReportGenerationContext.Provider>
  );
}

export function useReportGenerationContext() {
  const context = useContext(ReportGenerationContext);
  if (!context) {
    throw new Error(
      "ReportGenerationContext must be used within a ReportGenerationProvider",
    );
  }
  return context;
}
